import React, { useState } from 'react';
import {
    Popover,
    Typography,
    Alert
} from '@mui/material';
import ReservationForm, { ReservationFormData } from './ReservationForm';

interface Board {
    id: string;
    name: string;
    type: string;
}

interface Reservation {
    id: string;
    startDate: string;
    endDate: string;
    status: string;
    board: {
        id: string;
    };
}

interface AddReservationPopoverProps {
    anchorEl: HTMLElement | null;
    open: boolean;
    onClose: () => void;
    onAddSuccess: () => void;
    initialDate: Date | null;
    boards: Board[];
    existingReservations?: Reservation[];
    initialClient?: {
        name: string;
        email: string;
        phone?: string;
    };
}

const AddReservationPopover: React.FC<AddReservationPopoverProps> = ({
    anchorEl,
    open,
    onClose,
    onAddSuccess,
    initialDate,
    boards,
    existingReservations = [],
    initialClient
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (data: ReservationFormData) => {
        setLoading(true);
        setError('');

        try {
            const start = new Date(data.startDate);
            start.setHours(data.startTime.getHours(), data.startTime.getMinutes());

            const end = new Date(data.endDate);
            end.setHours(data.endTime.getHours(), data.endTime.getMinutes());

            const API_URL = process.env.REACT_APP_API_URL || 'https://api.turn.ar';

            let endpoint = `${API_URL}/api/boards/${data.boardId}/reservations`;

            const payload: any = {
                customerName: data.customerName,
                customerEmail: data.customerEmail,
                customerPhone: data.customerPhone,
                startDate: start.toISOString(),
                endDate: end.toISOString(),
                notes: data.notes,
                status: data.status,
                paymentStatus: data.paymentStatus
            };

            // Handle Recurring Flow
            if (data.isRecurring) {
                endpoint = `${API_URL}/api/boards/${data.boardId}/reservations/recurring`;

                // Add recurring specific fields
                payload.startTime = start.toISOString();
                payload.endTime = end.toISOString(); // Used for duration calculation
                payload.recurringPattern = data.recurringPattern;
                payload.recurringEndDate = data.recurringEndDate?.toISOString();
                payload.recurringDays = data.recurringDays;
            }

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(payload)
            });

            const resData = await response.json();

            if (!response.ok) {
                if (data.isRecurring && response.status === 201) {
                    // Recurring might return 201 even if there were SOME conflicts (handled by backend message?)
                } else {
                    throw new Error(resData.message || 'Error al crear la reserva');
                }
            }

            // Recurring endpoint returns { message, createdCount, conflictCount }
            if (data.isRecurring && resData.conflictCount > 0) {
                alert(`⚠️ Se crearon ${resData.createdCount} reservas. ${resData.conflictCount} fechas no se pudieron reservar por conflictos.`);
                // We use alert here for simplicity, or we could set a specific success "warning" 
                // but since we close the popover on success, alert is okay for now.
            }

            onAddSuccess();
            onClose();

        } catch (err: any) {
            console.error(err);
            setError(err.message || 'Error desconocido');
        } finally {
            setLoading(false);
        }
    };

    // Prepare initial data based on initialDate and initialClient
    const getInitialData = (): Partial<ReservationFormData> => {
        const now = new Date();
        const start = initialDate ? new Date(initialDate) : new Date();
        start.setHours(now.getHours() + 1, 0, 0, 0);
        const end = new Date(start);
        end.setHours(start.getHours() + 1);

        return {
            startDate: initialDate || new Date(),
            endDate: initialDate || new Date(),
            startTime: start,
            endTime: end,
            boardId: boards.length > 0 ? boards[0].id : '',
            status: 'confirmed',
            paymentStatus: 'pending',
            customerName: initialClient?.name || '',
            customerEmail: initialClient?.email || '',
            customerPhone: initialClient?.phone || ''
        };
    };

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
            PaperProps={{
                sx: { p: 3, width: 400, borderRadius: 2 }
            }}
        >
            <Typography variant="h6" fontWeight="bold" gutterBottom>
                Nueva Reserva
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <ReservationForm
                initialData={getInitialData()}
                onSubmit={handleSubmit}
                onCancel={onClose}
                boards={boards}
                existingReservations={existingReservations as any} // Type compatibility
                loading={loading}
            />
        </Popover>
    );
};

export default AddReservationPopover;
